import { DiffusionMessage, GetTimelineResponse } from '../apis/api-egg'
import { MeetingRoom, decodeMeetingToken } from '../pages/meeting-view/api'
import { PulseNotification, PulseOptions, PulseProps } from '../components/Pulse/PulseNotification'
import { UIContextType, useUI } from '../context/UIContext'
import { getSessionParameters, updateSessionParams } from '../helpers/urls'
import { useCallback, useEffect, useRef } from 'react'

import { ChangeMeetingText } from '../components/ChangeMeetingText'
import { CountDown } from '../components/CountDown'
import DayJS from '../helpers/DayJS'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NotificationWithCross } from '../components/NotificationWithCross'
import { PulseDTO } from '../apis/api-pulses'
import ReactDOMServer from 'react-dom/server'
import { ToastType } from 'react-hot-toast'
import { myPulseDataQueryKey } from '../pages/meeting-view/hooks/useMyPulseData'
import { useChangeMeeting } from './useChangeMeeting'
import { useNavigate } from 'react-router-dom'
import useParticipants from './useParticipants'
import { useQueryClient } from '@tanstack/react-query'
import { useSessionParams } from './useSessionParams'
import { useTimeline } from './useTimeline'
import { useTranslation } from 'react-i18next'
import { useZoomContext } from '../context/ZoomContext'
import { getSessionItem, removeSessionItem } from '../helpers/localStorage'
import { queryKeyHRExpertMentors } from './useHRExpertMentors'

export const useActions = () => {
    const { t } = useTranslation('notifications')
    const { pip, showToast, openPip } = useUI()
    const { currentEvent, nextEvent, isLoading } = useTimeline()
    const { changeMeeting, setRoomToSwitch } = useChangeMeeting()
    const { participants } = useParticipants()
    const {
        currentUserInfo: me,
        newDiffusionMessage,
        muteLocalAudio,
        unMuteLocalAudio,
        setUnReadMessages,
        timer,
    } = useZoomContext()

    const isChangingMeeting = useRef(false)
    const pendingRoom = useRef<string | null>(null)

    const { role } = useSessionParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    useEffect(() => {
        const { current } = timer
        if (!current) return
        return () => clearTimeout(current)
    }, [nextEvent, timer])

    useEffect(() => {
        const manuallySwitched = getSessionItem('manually-switched')
        if (manuallySwitched) return
        if (nextEvent) {
            const { room, date } = nextEvent
            setRoomToSwitch(room)
            const duration = DayJS(date).diff(DayJS(), 'milliseconds')
            if (room)
                actionDispatcher({
                    event: 'change-meeting',
                    payload: { room, duration },
                })
            else
                actionDispatcher({
                    event: 'meeting-end-in',
                    payload: { duration },
                })
        } else if (currentEvent) {
            const { room } = getSessionParameters()
            if (room && currentEvent.room !== room)
                actionDispatcher({
                    event: 'change-meeting',
                    payload: { room: currentEvent.room, duration: 5000 },
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEvent, nextEvent, isLoading])

    const actionDispatcher = useCallback(
        async ({ event, payload }: ActionsTypes) => {
            try {
                // if has payload and scope is not meet, return
                if (payload && 'scope' in payload && payload?.scope?.length && !payload.scope?.includes('meet')) return

                switch (event) {
                    case 'show-toast': {
                        if (!payload?.message) return
                        const { message, type, ...rest } = payload

                        if (type === 'custom' && typeof message === 'string')
                            showToast(<MessageInJSX message={message} />, rest)
                        else showToast(message, rest)
                        return
                    }

                    case 'redirect-to': {
                        payload.scope?.includes('meet') && navigate(payload.goTo)
                        break
                    }

                    case 'meeting-end-in': {
                        const { duration = 5000 } = payload
                        const text = t('actionDispatcher.meetingEndsIn')

                        const id = event
                        const time = DayJS().add(duration, 'milliseconds').toNow(true)
                        const icon = <EndIcon />

                        showToast.error(
                            <NotificationWithCross id={id}>
                                {text} {time}.{' '}
                            </NotificationWithCross>,
                            { id, duration, icon }
                        )

                        timer.current = setTimeout(() => {
                            navigate('/post-meeting/ended by host')
                            clearTimeout(timer.current)
                        }, duration)
                        break
                    }

                    case 'change-meeting': {
                        if (getSessionItem('manually-switched')) return
                        if (role !== 'student') return
                        const { duration = 5000, room } = payload
                        if (!room || duration <= 0) return

                        if (isChangingMeeting.current) {
                            console.log(`Blocked overlapping change-meeting for ${room}`)
                            return
                        }

                        if (timer.current) {
                            clearTimeout(timer.current)
                        }

                        pendingRoom.current = room

                        const id = `${event}-${room}`
                        const icon = <TimeIcon />

                        timer.current = setTimeout(async () => {
                            try {
                                isChangingMeeting.current = true
                                await changeMeeting(room)
                            } finally {
                                clearTimeout(timer.current)
                                isChangingMeeting.current = false
                                pendingRoom.current = null
                            }
                        }, duration)

                        showToast(
                            <NotificationWithCross id={id}>
                                <ChangeMeetingText room={room} duration={duration} />
                            </NotificationWithCross>,
                            { id, duration, icon }
                        )

                        break
                    }

                    case 'join-to-meeting': {
                        const { duration = 5000, meetingToken } = payload
                        const { room } = decodeMeetingToken(meetingToken)

                        const text =
                            room === 'general'
                                ? t('actionDispatcher.changingToGeneralRoom')
                                : t('actionDispatcher.changingToTeamRoom')

                        const id = `${event}-${room}`
                        const time = DayJS().add(duration, 'milliseconds').toNow(true)
                        const icon = <TimeIcon />
                        const msg = (
                            <div>
                                {text} {t('actionDispatcher.in')} {time}. (
                                <small>
                                    <CountDown duration={duration} />
                                </small>
                                )
                            </div>
                        )
                        showToast(msg, { id, duration, icon })

                        timer.current = setTimeout(() => {
                            changeMeeting(room)
                            clearTimeout(timer.current)
                        }, duration)

                        break
                    }

                    case 'ui-team-has-updated': {
                        await queryClient.invalidateQueries({ queryKey: ['Meeting Data'] })
                        const params = getSessionParameters()
                        const { _algorithmId, forceUpdate } = payload
                        if (params._algorithmId === _algorithmId && !forceUpdate) return
                        if (_algorithmId) updateSessionParams({ _algorithmId })
                        if (forceUpdate && params.room === 'teams' && !isChangingMeeting.current) {
                            if (timer.current) {
                                clearTimeout(timer.current)
                                pendingRoom.current = null
                            }
                            isChangingMeeting.current = true
                            try {
                                await changeMeeting('teams')
                            } finally {
                                isChangingMeeting.current = false
                            }
                        }

                        queryClient.invalidateQueries({ queryKey: myPulseDataQueryKey })
                        break
                    }

                    case 'new-timeline-event': {
                        removeSessionItem('manually-switched')

                        queryClient.setQueryData<GetTimelineResponse['data']>(
                            ['Course', 'Timeline', payload._courseId],
                            payload
                        )
                        break
                    }

                    case 'ui-reload': {
                        window.location.reload()
                        break
                    }

                    case 'play-external-content': {
                        newDiffusionMessage(payload)

                        const icon = (
                            <FontAwesomeIcon
                                className="icon"
                                icon={['far', 'megaphone']}
                                style={{ color: '#00A3FF' }}
                            />
                        )
                        const duration = 10_000

                        if (payload.type === 'text') {
                            const { message } = payload
                            if (!message) return console.warn('❗ No `message` in play-external-content:', { payload })

                            const id = Date.now().toString()

                            setUnReadMessages(prev => ({ ...prev, diffusion: true }))
                            return showToast(<NotificationWithCross id={id}>{payload.message}</NotificationWithCross>, {
                                id,
                                icon,
                                duration,
                            })
                        }

                        const { src } = payload

                        if (!src) return console.warn('❗ No `src` in play-external-content:', { payload })
                        if (pip?.src === src) return

                        const id = src
                        showToast(
                            <NotificationWithCross id={id}>
                                {t('actionDispatcher.broadcastMessageInStart')}
                                <CountDown key={`${id}-${Date.now()}`} duration={duration} format="s" />
                                {t('actionDispatcher.broadcastMessageInEnd')}
                            </NotificationWithCross>,
                            { icon, duration, id }
                        )

                        setTimeout(() => {
                            openPip({
                                ...payload,
                                onMute: muteLocalAudio,
                                onUnmute: unMuteLocalAudio,
                            })
                            setUnReadMessages(prev => ({ ...prev, diffusion: true }))
                        }, duration)

                        break
                    }

                    case 'new-pulse': {
                        if (!me) return console.warn('❗ No `me` in new-pulse')

                        const { giver, receiver } = payload

                        const isMyTable = participants.some(
                            ({ userIdentity }) =>
                                userIdentity === giver.profileId || userIdentity === receiver.profileId
                        )

                        queryClient.setQueriesData<PulseDTO[]>(
                            { queryKey: ['Pulses'], exact: false, type: 'all' },
                            old =>
                                old
                                    ? old.map(pulse => {
                                          if (pulse.id === giver.profileId)
                                              return {
                                                  ...pulse,
                                                  given: pulse.given + 1,
                                              }
                                          else if (pulse.id === receiver.profileId)
                                              return {
                                                  ...pulse,
                                                  received: pulse.received + 1,
                                              }
                                          return pulse
                                      })
                                    : old
                        )

                        if (!isMyTable) return

                        const message = ReactDOMServer.renderToString(<PulseNotification me={me} {...payload} />)

                        showToast(message, PulseOptions)
                        newDiffusionMessage({
                            type: 'text',
                            message,
                        })

                        break
                    }

                    case 'meet-mentor-joined':
                    case 'meet-mentor-left': {
                        queryClient.invalidateQueries({ queryKey: queryKeyHRExpertMentors })
                        break
                    }

                    default: {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const exhaustiveCheck: never = event
                        // console.warn('❗ Action not found:', { type: event, payload })
                    }
                }
            } catch (error) {
                console.error('❗ Error in actionDispatcher:', { type: event, payload, error })
            }
        },
        [
            changeMeeting,
            queryClient,
            setUnReadMessages,
            openPip,
            muteLocalAudio,
            navigate,
            pip,
            role,
            showToast,
            t,
            newDiffusionMessage,
            unMuteLocalAudio,
            me,
            participants,
            timer,
        ]
    )

    return { actionDispatcher }
}

const MessageInJSX = ({ message }: { message: string }) => <div dangerouslySetInnerHTML={{ __html: message }}></div>

const TimeIcon = () => <FontAwesomeIcon icon={['far', 'clock']} className="icon" style={{ color: '#00A3FF' }} />
const EndIcon = () => <FontAwesomeIcon icon={['far', 'hourglass-end']} className="icon" style={{ color: '#ff647c' }} />

type ActionsTypes =
    | {
          event: 'show-toast'
          payload?: {
              scope?: string[]
              type?: ToastType
              message: string
          } & Parameters<UIContextType['showToast']>[1]
      }
    | {
          event: 'meeting-end-in'
          payload: {
              duration: number
          }
      }
    | {
          event: 'redirect-to'
          payload: {
              goTo: string
              scope?: string[]
          }
      }
    | {
          event: 'change-meeting'
          payload: {
              room: MeetingRoom
              duration?: number
          }
      }
    | {
          event: 'join-to-meeting'
          payload: {
              meetingToken: string
              duration?: number
          }
      }
    | {
          event: 'ui-team-has-updated'
          payload: {
              _algorithmId?: string
              forceUpdate?: boolean
              courseId?: string
          }
      }
    | {
          event: 'new-timeline-event'
          payload: GetTimelineResponse['data']
      }
    | {
          event: 'play-external-content'
          payload: DiffusionMessage
      }
    | {
          event: 'ui-reload'
          payload: null
      }
    | {
          event: 'new-pulse'
          payload: PulseProps
      }
    | {
          event: 'meet-mentor-joined' | 'meet-mentor-left'
          payload: {}
      }
